import {
    Col,
    Form,
    FormInstance,
    Input,
    Radio,
    RadioChangeEvent,
    Row,
    Space,
    notification,
} from 'antd';
import React, { FC, useState } from 'react';
import service from '../../../services/isnaportalsync';
import { checkAuthorization } from '../../check-authorization/check-authorization';
import { localize } from '../../helpers';
import Button from '../button/button';
import './index.scss';

interface TProps {
    form?: FormInstance;
    iinBin?: boolean;
    onSuccess?: (data: { message: string; iin: string }) => void;
    translations: Record<string, string>;
    user: {
        iin?: string;
    };
    checkLoginNeedle: string;
    language: string;
}

const KdpRequest: FC<TProps> = ({
    form,
    iinBin,
    onSuccess,
    translations,
    user,
    checkLoginNeedle,
    language,
}) => {
    const [status, setStatus] = useState<'SUCCESS' | 'WAITING' | 'FAILED' | null>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [radioType, setRadioType] = useState<number>(1);
    const [loading, setLoading] = useState(false);

    const onRadioChange = (e: RadioChangeEvent) => {
        setRadioType(e.target.value);
        setStatus(null);
    };

    const handleRequest = async () => {
        if (!user.iin) {
            checkAuthorization(null, language, checkLoginNeedle, translations);
            return;
        }

        try {
            await form.validateFields(['iin']);
        } catch (err) {
            return;
        }

        if (user.iin !== form.getFieldValue('iin')) {
            notification['error']({
                message: localize('kdp-request-error-iin-mismatch', translations),
            });
            return;
        }

        setLoading(true);

        service
            .kdpStatus('?iin=' + form.getFieldValue('iin'))
            .then((res) => {
                if (res.status === 200) {
                    if (res.data.status === 'PENDING') {
                        setStatus('WAITING');
                        notification['success']({
                            message: localize('kdp-request-query-sent', translations),
                            description: localize(
                                'kdp-request-max-response-waiting-time',
                                translations
                            ),
                        });
                    } else {
                        setStatus('SUCCESS');
                        onSuccess({
                            message: res.data.message,
                            iin: form.getFieldValue('iin'),
                        });
                        notification['success']({
                            message: localize('kdp-request-success', translations),
                        });
                    }
                }
            })
            .catch((err) => {
                setErrorMessage(err.response.data.message);
                if (err.response.data.message === 'Ожидание ответа субъекта персональных данных') {
                    setStatus('WAITING');
                    notification['success']({
                        message: localize('kdp-request-query-sent', translations),
                        description: localize(
                            'kdp-request-max-response-waiting-time',
                            translations
                        ),
                    });
                } else {
                    notification['error']({
                        message: localize('kdp-request-error', translations),
                        description: err.response?.data?.message,
                    });
                    setStatus('FAILED');
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Row className="kdp-request">
            <Col
                xs={24}
                md={24}
                lg={24}
            >
                <Row>
                    {iinBin && (
                        <Col
                            xs={24}
                            md={3}
                            lg={3}
                        >
                            <Radio.Group
                                onChange={onRadioChange}
                                value={radioType}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>
                                        {localize('kdp-request-iin', translations)}
                                    </Radio>
                                    <Radio value={2}>
                                        {localize('kdp-request-iin', translations)}
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        </Col>
                    )}
                    {radioType === 1 ? (
                        <Col
                            xs={24}
                            md={17}
                            lg={17}
                        >
                            <span className="kdp-gray">
                                {localize('kdp-request-personal-data-request', translations)}
                            </span>
                        </Col>
                    ) : null}
                </Row>
            </Col>
            <Col
                xs={24}
                md={16}
                lg={8}
            >
                {radioType === 1 ? (
                    <Form.Item label={localize('kdp-request-iin', translations)}>
                        <Input.Group
                            compact
                            size="large"
                        >
                            <Form.Item
                                noStyle
                                name="iin"
                                rules={[
                                    {
                                        required: true,
                                        message: localize('error.iin-bin-length-12', translations),
                                    },
                                    {
                                        pattern: /^[0-9]{12,12}$/,
                                        message: localize('error.iin-bin-length-12', translations),
                                        len: 12,
                                    },
                                ]}
                            >
                                <Input
                                    style={{ width: '50%' }}
                                    size="large"
                                    placeholder={localize('placeholder.input', translations)}
                                    maxLength={12}
                                    onSubmit={(e) => e?.preventDefault()}
                                    onPressEnter={(e) => {
                                        e?.preventDefault();
                                        handleRequest();
                                    }}
                                />
                            </Form.Item>
                            <Button
                                color="gold"
                                htmlType="button"
                                onClick={handleRequest}
                                loading={loading}
                            >
                                {localize('button.access-request', translations)}
                            </Button>
                        </Input.Group>
                    </Form.Item>
                ) : radioType === 2 ? (
                    <Form.Item
                        label={localize('kdp-request-iin', translations)}
                        name="bin"
                        rules={[
                            {
                                pattern: /^[0-9]{12,12}$/,
                                message: localize('error.iin-bin-length-12', translations),
                                len: 12,
                            },
                        ]}
                    >
                        <Input
                            size="large"
                            placeholder={localize('placeholder.input', translations)}
                        />
                    </Form.Item>
                ) : null}
                <div className="kdp">
                    {status === 'WAITING' ? (
                        <>
                            <div>{localize('kdp-request-access-status', translations)}</div>
                            <div className="kdp-yellow">
                                {localize('kdp-request-query-sent-kdp', translations)}
                            </div>
                        </>
                    ) : status === 'SUCCESS' ? (
                        <span className="kdp-green">
                            {localize('kdp-request-success', translations)}
                        </span>
                    ) : status === 'FAILED' ? (
                        <>
                            <div className="kdp-red">{errorMessage}</div>
                            <div className="kdp-gray">
                                {localize('kdp-request-further-access-to-service', translations)}
                            </div>
                        </>
                    ) : null}
                </div>
            </Col>
        </Row>
    );
};

export default KdpRequest;
